/* eslint-disable max-lines */
import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';
import Uppy from '@uppy/core';
import Audio from '@uppy/audio';
import OneDrive from '@uppy/onedrive';
import ScreenCapture from '@uppy/screen-capture';
import Tus from '@uppy/tus';
import Webcam from '@uppy/webcam';
import { useGetModels } from '@utils/api/models/use-get-models.ts';
import { useFolderTree } from '@utils/api/folders/use-folder-tree';
import { flattenFolderTree } from '@utils/folders/folder-tree';
import { useCreateSTTSession } from '@utils/api/sessions/use-create-stt-session.tsx';
import {
  withFairUseRetry,
  isFairUseSoftLimit
} from '@utils/api/fair-use-retry';
import { isUploadSuspendedError } from '@utils/api/upload-suspension.ts';
import { useCreateAlignmentSession } from '@utils/api/sessions/use-create-alignment-session.tsx';
import { useUploadAlignment } from '@utils/api/sessions/use-upload-alignment.tsx';
import { useRequestUploadUrl } from '@utils/api/sessions/use-request-upload-url.tsx';
import { useUploadToAzureBlob } from '@utils/api/sessions/use-upload-to-azure-blob.tsx';
import { useConfirmUploadComplete } from '@utils/api/sessions/use-confirm-upload-complete.tsx';
import { useOrganization } from '@utils/api/users/use-get-organization.tsx';
import {
  API_BASE_URL,
  AUDIO_SOURCE_FILE_EXTENSIONS,
  DOCUMENT_SOURCE_FILE_EXTENSIONS,
  MAX_FILE_SIZE,
  SUBTITLE_SOURCE_FILE_EXTENSIONS,
  VIDEO_SOURCE_FILE_EXTENSIONS
} from '@utils/constants/app-constants.ts';
import { TokenExtension } from '@utils/extensions/token.extension.ts';
import { useLanguageDetection } from '@utils/hooks/use-language-detection.ts';
import { useEntitlements } from '@utils/providers/entitlements-context.tsx';
import { DocumentTypeOptions } from '@utils/types/editors/utils.ts';
import { Language } from '@utils/types/shared/language.ts';
import UrlPlugin from '@components/shared/upload/url-plugin.tsx';
import { initialState, reducer } from './upload-state';

type Params = { folderId?: string };

// Max file size for direct Azure upload (2GB) - larger files must use TUS
const MAX_DIRECT_UPLOAD_SIZE = 2 * 1024 * 1024 * 1024;

export function useUpload({
  type,
  setType,
  initialFiles,
  initialFolderId
}: {
  type: DocumentTypeOptions | 'align' | '';
  setType: (type: DocumentTypeOptions | 'align' | '') => void;
  initialFiles?: File[];
  initialFolderId?: number;
}) {
  const { t } = useTranslation();
  const { folderId } = useParams<Params>();
  const queryClient = useQueryClient();

  const [state, dispatch] = useReducer(reducer, initialState);
  const [activeType, setActiveType] = useState<'document' | 'caption'>(
    type === 'caption' ? 'caption' : 'document'
  );

  const [diarization, setDiarization] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Opt-in, not the default. Direct upload PUTs from the browser straight to
  // object storage; it cannot resume, so a dropped connection loses the whole
  // file. tus uploads via our own API and survives drops, which is why the UI
  // tells people to leave this off unless they have a reason.
  //
  // It used to default to true, so most uploads took the direct path — and
  // when that path broke after the S3 cutover, nearly every upload failed
  // rather than an opt-in minority. Defaulting to the resumable route bounds
  // the blast radius of anything wrong with this one.
  const [useDirectAzureUpload, setUseDirectAzureUpload] = useState(false);
  const [hasLargeFiles, setHasLargeFiles] = useState(false);
  const [speakerValidationError, setSpeakerValidationError] =
    useState<string>('');

  const parsedUrlFolderId =
    folderId && !isNaN(parseInt(folderId)) ? parseInt(folderId) : null;
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(
    initialFolderId ?? parsedUrlFolderId
  );

  const { data: folderTree } = useFolderTree(type !== '');
  const folderOptions = useMemo(
    () => [
      { label: t('home'), value: '' },
      ...flattenFolderTree(folderTree).map(({ label, value }) => ({
        label,
        value
      }))
    ],
    [folderTree, t]
  );

  const { data: models } = useGetModels();
  const { data: organization } = useOrganization();
  const { refreshEntitlements } = useEntitlements();

  const createSTTSessionMutation = useCreateSTTSession();
  const createAlignmentSessionMutation = useCreateAlignmentSession();
  const uploadAlignmentMutation = useUploadAlignment();
  const requestUploadUrlMutation = useRequestUploadUrl();
  const uploadToAzureBlobMutation = useUploadToAzureBlob();
  const confirmUploadCompleteMutation = useConfirmUploadComplete();

  const [detectionFile, setDetectionFile] = useState<File | null>(null);
  const userOverrodeLanguageRef = useRef(false);
  const detection = useLanguageDetection(detectionFile);

  const fileUuidsRef = useRef<Map<string, string>>(new Map());
  const onLargeFileCheckRef = useRef<((hasLarge: boolean) => void) | null>(
    null
  );

  const [allowedFileTypes, setAllowedFileTypes] = useState<string[]>([
    'video/*',
    'audio/*',
    ...VIDEO_SOURCE_FILE_EXTENSIONS,
    ...AUDIO_SOURCE_FILE_EXTENSIONS
  ]);

  useEffect(() => {
    if (organization) setDiarization(organization?.diarization || false);
  }, [organization]);

  useEffect(() => {
    if (activeType === 'caption') {
      setAllowedFileTypes([
        'video/*',
        'audio/*',
        ...VIDEO_SOURCE_FILE_EXTENSIONS,
        ...AUDIO_SOURCE_FILE_EXTENSIONS,
        ...SUBTITLE_SOURCE_FILE_EXTENSIONS
      ]);
    } else {
      setAllowedFileTypes([
        'video/*',
        'audio/*',
        ...VIDEO_SOURCE_FILE_EXTENSIONS,
        ...AUDIO_SOURCE_FILE_EXTENSIONS,
        ...DOCUMENT_SOURCE_FILE_EXTENSIONS
      ]);
    }
  }, [activeType]);

  const getFilenameFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const segments = pathname.split('/').filter(s => s.length > 0);
      const lastSegment = segments[segments.length - 1];
      if (lastSegment && lastSegment.includes('.'))
        return decodeURIComponent(lastSegment);
      const hostname = urlObj.hostname.replace('www.', '');
      if (segments.length > 0)
        return `${hostname}_${segments[segments.length - 1]}`;
      return hostname;
    } catch {
      return 'pasted_link';
    }
  };

  const generateDefaultFilename = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    uppyFile: any,
    file?: File | Blob
  ): string => {
    if (file && 'name' in file && file.name && file.name.trim() !== '')
      return file.name;
    if (uppyFile.name && uppyFile.name.trim() !== '') return uppyFile.name;

    const timestamp = new Date()
      .toISOString()
      .replace(/[:.]/g, '-')
      .slice(0, -5);
    const source = uppyFile.source || uppyFile.meta?.source || 'unknown';

    const getExtension = (mimeType: string): string => {
      if (!mimeType) return '';
      if (mimeType.includes('webm')) return '.webm';
      if (mimeType.includes('mp4')) return '.mp4';
      if (mimeType.includes('mpeg')) return '.mp3';
      if (mimeType.includes('wav')) return '.wav';
      if (mimeType.includes('ogg')) return '.ogg';
      if (mimeType.includes('video')) return '.mp4';
      if (mimeType.includes('audio')) return '.mp3';
      return '';
    };

    const fileType = file?.type || uppyFile.type || uppyFile.meta?.type || '';
    const extension = getExtension(fileType);

    switch (source.toLowerCase()) {
      case 'webcam':
        return `webcam-recording-${timestamp}${extension || '.webm'}`;
      case 'audio':
        return `audio-recording-${timestamp}${extension || '.webm'}`;
      case 'screencapture':
      case 'screen-capture':
        return `screen-recording-${timestamp}${extension || '.webm'}`;
      case 'urlplugin': {
        const url = uppyFile.meta?.url || uppyFile.remote?.url;
        return url ? getFilenameFromUrl(url) : `pasted-link-${timestamp}`;
      }
      default:
        return `recording-${timestamp}${extension}`;
    }
  };

  const validateSpeakerConstraints = (): boolean => {
    setSpeakerValidationError('');
    if (state.minSpeakers !== null && state.minSpeakers !== undefined) {
      if (state.minSpeakers < 1 || state.minSpeakers > 1000) {
        setSpeakerValidationError(
          t('min-speakers-must-be-between-1-and-1000') ||
            'Min speakers must be between 1 and 1000'
        );
        return false;
      }
    }
    if (state.maxSpeakers !== null && state.maxSpeakers !== undefined) {
      if (state.maxSpeakers < 1 || state.maxSpeakers > 1000) {
        setSpeakerValidationError(
          t('max-speakers-must-be-between-1-and-1000') ||
            'Max speakers must be between 1 and 1000'
        );
        return false;
      }
    }
    if (
      state.minSpeakers !== null &&
      state.minSpeakers !== undefined &&
      state.maxSpeakers !== null &&
      state.maxSpeakers !== undefined &&
      state.minSpeakers > state.maxSpeakers
    ) {
      setSpeakerValidationError(
        t('min-speakers-cannot-exceed-max-speakers') ||
          'Min speakers cannot exceed max speakers'
      );
      return false;
    }
    return true;
  };

  const validateAlignmentMediaFile = (
    file: File
  ): { valid: boolean; error?: string } => {
    const maxSize = 2 * 1024 * 1024 * 1024;
    if (file.size > maxSize)
      return { valid: false, error: t('file-size-exceed') };
    if (file.size === 0) return { valid: false, error: 'Media file is empty' };
    return { valid: true };
  };

  const validateAlignmentTranscriptFile = (
    file: File
  ): { valid: boolean; error?: string } => {
    const maxSize = 10 * 1024 * 1024;
    const validExtensions = ['.txt', '.vtt', '.srt', '.sbv'];
    const extension = file.name.toLowerCase().match(/\.[^.]+$/)?.[0];
    if (!extension || !validExtensions.includes(extension)) {
      return {
        valid: false,
        error:
          'Invalid transcript file type. Please upload TXT, VTT, SRT, or SBV files.'
      };
    }
    if (file.size > maxSize)
      return { valid: false, error: 'Transcript file is too large (max 10MB)' };
    if (file.size === 0)
      return { valid: false, error: 'Transcript file is empty' };
    return { valid: true };
  };

  const initUppy = useMemo(() => {
    const authToken = TokenExtension.get();

    const uppyInstance = new Uppy({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onBeforeFileAdded: (currentFile: any, files: any) => {
        const length = Object.keys(files).length;
        const maxFileSize = 40 * 1024 * 1024 * 1024;
        if (length > 0 && currentFile.name && currentFile.name.length > 0) {
          if (currentFile.size && currentFile.size > maxFileSize) {
            toast.error(t('file-size-exceed'));
            return false;
          }
        }
        return true;
      },
      restrictions: {
        allowedFileTypes: [
          'video/*',
          'audio/*',
          ...VIDEO_SOURCE_FILE_EXTENSIONS,
          ...AUDIO_SOURCE_FILE_EXTENSIONS
        ],
        maxTotalFileSize: parseInt(MAX_FILE_SIZE)
      }
    })
      .use(Tus, {
        endpoint: `${API_BASE_URL}/api/v3/files/`,
        retryDelays: [0, 3000, 5000, 10000, 20000, 30000, 60000],
        removeFingerprintOnSuccess: false,
        storeFingerprintForResuming: true,
        // 5 MiB exactly — the minimum S3 multipart part size. Each PATCH
        // becomes one UploadPart against OVH object storage, and a
        // non-final part below this fails CompleteMultipartUpload with
        // EntityTooSmall. Keep binary MiB: 5 * 1000 * 1000 is under the
        // limit and would break every multi-chunk upload.
        chunkSize: 5 * 1024 * 1024,
        // Deterministic, derived only from the file itself.
        //
        // This is the key tus stores upload URLs under, so it is what makes
        // storeFingerprintForResuming work. It used to include a random uuid
        // tied to file.id, which meant re-adding the same file produced a new
        // fingerprint and therefore a brand new upload — resume could never
        // engage, and every restart created another session server-side.
        //
        // lastModified is included so two genuinely different files that happen
        // to share a name and size stay distinct.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        fingerprint: (file: any) =>
          Promise.resolve(
            [
              'tus',
              file.name || '',
              file.type || '',
              String(file.size || ''),
              String(file.data?.lastModified || '')
            ].join('-')
          ),
        headers: { Authorization: `Bearer ${authToken.string}` },
        withCredentials: true
      })
      .use(UrlPlugin, { title: t('paste-link') })
      .use(OneDrive, { companionUrl: 'https://companion.scriptix.dev' })
      .use(Webcam, {
        showVideoSourceDropdown: false,
        showRecordingLength: true,
        modes: ['video-audio'],
        preferredVideoMimeType: 'video/webm'
      })
      .use(Audio, { showAudioSourceDropdown: false });

    if (typeof navigator.mediaDevices?.getDisplayMedia === 'function') {
      uppyInstance.use(ScreenCapture, { preferredVideoMimeType: 'video/webm' });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    uppyInstance.on('file-added', (file: any) => {
      if (!fileUuidsRef.current.has(file.id)) {
        const newUuid = uuidv4();
        fileUuidsRef.current.set(file.id, newUuid);
        uppyInstance.setFileMeta(file.id, { uuid: newUuid });
      }
      if (!file.name || file.name.trim() === '') {
        const defaultName = generateDefaultFilename(file, file.data);
        uppyInstance.setFileMeta(file.id, { name: defaultName });
        file.name = defaultName;
      }
      const filesCount = uppyInstance.getFiles().length;
      dispatch({ type: 'SET_HAS_FILES', payload: filesCount > 0 });
      if (file.data instanceof File) setDetectionFile(file.data);
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    uppyInstance.on('file-removed', (file: any) => {
      fileUuidsRef.current.delete(file.id);
      const filesCount = uppyInstance.getFiles().length;
      dispatch({ type: 'SET_HAS_FILES', payload: filesCount > 0 });
      if (filesCount === 0) {
        setDetectionFile(null);
        userOverrodeLanguageRef.current = false;
      }
    });

    const checkLargeFiles = () => {
      const files = uppyInstance.getFiles();

      const hasLarge = files.some(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (f: any) => f.source !== 'UrlPlugin' && f.size > MAX_DIRECT_UPLOAD_SIZE
      );
      onLargeFileCheckRef.current?.(hasLarge);
    };
    uppyInstance.on('file-added', checkLargeFiles);
    uppyInstance.on('file-removed', checkLargeFiles);
    uppyInstance.on('complete', result => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      result.successful?.forEach((file: any) =>
        fileUuidsRef.current.delete(file.id)
      );
      const filesCount = uppyInstance.getFiles().length;
      dispatch({ type: 'SET_HAS_FILES', payload: filesCount > 0 });
    });

    uppyInstance.on('upload', () => {
      dispatch({ type: 'SET_UPLOADING', payload: true });
      dispatch({ type: 'SET_IS_PAUSED', payload: false });
    });
    uppyInstance.on('pause-all', () =>
      dispatch({ type: 'SET_IS_PAUSED', payload: true })
    );
    uppyInstance.on('resume-all', () =>
      dispatch({ type: 'SET_IS_PAUSED', payload: false })
    );
    uppyInstance.on('complete', () => {
      dispatch({ type: 'SET_UPLOADING', payload: false });
      dispatch({ type: 'SET_IS_PAUSED', payload: false });
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    uppyInstance.on('upload-error', (_file: any, error: any) => {
      const isNetworkError =
        error?.message?.includes('network') ||
        error?.message?.includes('NetworkError') ||
        error?.message?.includes('Failed to fetch') ||
        error?.message?.includes('blocked') ||
        error?.isNetworkError ||
        (error?.request && !error?.response);
      if (isNetworkError) {
        dispatch({ type: 'SET_UPLOADING', payload: false });
        dispatch({ type: 'SET_IS_PAUSED', payload: true });
      }
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    uppyInstance.on('error', (error: any) => {
      const msg: string = error?.message ?? '';
      if (
        msg.includes('Microphone access not supported') ||
        msg.includes('Screen recorder access not supported') ||
        msg.includes('Screen capture is not supported') ||
        msg.includes('getDisplayMedia') ||
        msg.includes('getUserMedia')
      ) {
        toast.error(t('browser-does-not-support-recording'));
      }

      // OneDrive / companion service error — show a user-facing message
      // instead of silently failing (Sentry issues 2W4 / 2YK).
      if (
        msg.includes('companion') ||
        msg.includes('onedrive') ||
        msg.includes('Could not GET') ||
        msg.includes('companion.scriptix.dev')
      ) {
        toast.error(t('onedrive-connection-failed'));
      }
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return uppyInstance as any;
  }, [t]);

  useEffect(() => {
    if (state.uppy && allowedFileTypes.length > 0) {
      state.uppy.setOptions({
        restrictions: {
          allowedFileTypes,
          maxTotalFileSize: parseInt(MAX_FILE_SIZE)
        }
      });
    }
  }, [allowedFileTypes, state.uppy]);

  useEffect(() => {
    dispatch({ type: 'SET_UPPY', payload: initUppy });
    onLargeFileCheckRef.current = (hasLarge: boolean) => {
      setHasLargeFiles(hasLarge);
      if (hasLarge) setUseDirectAzureUpload(false);
    };
    return () => {
      onLargeFileCheckRef.current = null;
    };
  }, [initUppy]);

  useEffect(() => {
    if (!state.uppy || !initialFiles?.length) return;
    initialFiles.forEach(file => {
      try {
        state.uppy!.addFile({
          name: file.name,
          type: file.type,
          data: file,
          source: 'Local',
          isRemote: false
        });
      } catch {
        // Uppy displays duplicate and restriction errors; continue with the rest.
      }
    });
  }, [state.uppy, initialFiles]);

  const getFiles = () => state.uppy?.getFiles() ?? [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tusFiles = getFiles().filter((f: any) => f.source !== 'UrlPlugin');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const urlFiles = getFiles().filter((f: any) => f.source === 'UrlPlugin');
  const hasUrlFiles = urlFiles.length > 0;

  useEffect(() => {
    // Pasted links never PUT from the browser — uploadUrlFile hands the URL to
    // the API — so this only drives which controls the panel shows.
    if (hasUrlFiles) setUseDirectAzureUpload(true);
  }, [hasUrlFiles]);

  const hasActiveTusUpload =
    tusFiles.some(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (f: any) => f?.progress?.uploadStarted && !f?.progress?.uploadComplete
    ) || false;

  const canStartUpload =
    !!state.selectedLanguage &&
    activeType !== undefined &&
    (tusFiles.length > 0 || urlFiles.length > 0);

  const primaryUploadDisabled = !canStartUpload || isSubmitting;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const uploadUrlFile = async (urlFile: any) => {
    if (!state.selectedLanguage) {
      toast.error(t('please-select-language'));
      return false;
    }
    try {
      const mediaUrl = urlFile.meta?.url || urlFile.remote?.url;
      if (!mediaUrl) {
        toast.error(t('invalid-url'));
        return false;
      }
      const filename = urlFile.name || getFilenameFromUrl(mediaUrl);
      if (diarization && !validateSpeakerConstraints()) return false;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const payload: any = {
        language: state.selectedLanguage.key,
        keep_source: true,
        media_source: mediaUrl,
        diarization,
        document: { document_type: activeType, filename },
        punctuation: true,
        multichannel: false,
        folder_id: selectedFolderId
      };
      if (diarization) {
        if (state.minSpeakers != null) payload.min_speakers = state.minSpeakers;
        if (state.maxSpeakers != null) payload.max_speakers = state.maxSpeakers;
      }
      if (state.selectedExportTemplate?.id)
        payload.export_template_id = state.selectedExportTemplate.id;

      await withFairUseRetry(() =>
        createSTTSessionMutation.mutateAsync(payload)
      );
      return true;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (isUploadSuspendedError(error)) {
        toast.error(t('uploading-suspended-tooltip'));
        refreshEntitlements();
        return false;
      }
      const status = error?.response?.status;
      if (!isFairUseSoftLimit(error) && status !== 429) {
        toast.error(
          error?.response?.data?.error ||
            t('network-error-please-try-again-or-contact-support')
        );
      }
      return false;
    }
  };

  const invalidateAfterUpload = () => {
    if (selectedFolderId !== null) {
      queryClient.invalidateQueries(['folder', String(selectedFolderId)]);
    } else {
      queryClient.invalidateQueries(['folders']);
    }
  };

  const uploadDirectAzure = async () => {
    if (!state.uppy) return;
    if (!state.selectedLanguage) {
      toast.error(t('please-select-language'));
      return;
    }
    const files = getFiles();
    if (files.length === 0) {
      toast.error(t('please-add-a-file-or-finish-recording'));
      return;
    }

    dispatch({ type: 'SET_UPLOADING', payload: true });
    dispatch({ type: 'SET_AZURE_BLOB_PROGRESS', payload: 0 });
    setIsSubmitting(true);

    try {
      if (urlFiles.length > 0) {
        for (const urlFile of urlFiles) {
          const success = await uploadUrlFile(urlFile);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if (success) state.uppy!.removeFile((urlFile as any).id);
        }
        if (tusFiles.length === 0) {
          toast.success(
            activeType === 'document'
              ? t('transcript-created-successfully')
              : t('caption-created-successfully')
          );
          invalidateAfterUpload();
          dispatch({ type: 'SET_UPLOADING', payload: false });
          dispatch({ type: 'SET_AZURE_BLOB_PROGRESS', payload: 0 });
          setIsSubmitting(false);
          onClose();
          return;
        }
      }

      for (const uppyFile of tusFiles) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const file = (uppyFile as any).data as File;
        const filename = generateDefaultFilename(uppyFile, file);
        if (diarization && !validateSpeakerConstraints()) {
          // Match the pattern used elsewhere in this file (lines 545, 719):
          // surface validation errors via toast and bail. The previous `throw
          // new Error(speakerValidationError)` read the state var from closure
          // on the same tick its setter was called — it was always stale (""),
          // and the throw landed in Sentry as a useless "Error:" / "Error:
          // undefined" with no actionable context.
          dispatch({ type: 'SET_UPLOADING', payload: false });
          dispatch({ type: 'SET_AZURE_BLOB_PROGRESS', payload: 0 });
          setIsSubmitting(false);
          return;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const payload: any = {
          language: state.selectedLanguage.key,
          keep_source: true,
          diarization,
          document: { document_type: activeType, filename },
          punctuation: true,
          multichannel: false,
          folder_id: selectedFolderId
        };
        if (diarization) {
          if (state.minSpeakers != null)
            payload.min_speakers = state.minSpeakers;
          if (state.maxSpeakers != null)
            payload.max_speakers = state.maxSpeakers;
        }
        if (state.selectedExportTemplate?.id)
          payload.export_template_id = state.selectedExportTemplate.id;

        const sessionResponse = await withFairUseRetry(() =>
          createSTTSessionMutation.mutateAsync(payload)
        );
        const sessionId = sessionResponse.result.session_id;
        const uploadUrlResponse = await requestUploadUrlMutation.mutateAsync({
          sessionId
        });
        await uploadToAzureBlobMutation.mutateAsync({
          uploadUrl: uploadUrlResponse.result.upload_url,
          file,
          // Lets the upload run chunked and resumable on S3, and lets a failure
          // be reported against the session instead of vanishing.
          sessionId,
          onProgress: progress =>
            dispatch({ type: 'SET_AZURE_BLOB_PROGRESS', payload: progress })
        });
        await confirmUploadCompleteMutation.mutateAsync({ sessionId });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        state.uppy.removeFile((uppyFile as any).id);
      }

      toast.success(
        activeType === 'document'
          ? t('transcript-created-successfully')
          : t('caption-created-successfully')
      );
      invalidateAfterUpload();
      onClose();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (isUploadSuspendedError(error)) {
        toast.error(t('uploading-suspended-tooltip'));
        refreshEntitlements();
      } else {
        const status = error?.response?.status;
        if (!isFairUseSoftLimit(error) && status !== 429) {
          toast.error(
            error?.response?.data?.error ||
              error?.message ||
              t('network-error-please-try-again-or-contact-support')
          );
        }
      }
    } finally {
      dispatch({ type: 'SET_UPLOADING', payload: false });
      dispatch({ type: 'SET_AZURE_BLOB_PROGRESS', payload: 0 });
      setIsSubmitting(false);
    }
  };

  const uploadTus = async () => {
    if (!state.uppy) return;
    if (!state.selectedLanguage) {
      toast.error(t('please-select-language'));
      return;
    }
    if (tusFiles.length === 0 && urlFiles.length === 0) {
      toast.error(t('please-add-a-file-or-finish-recording'));
      return;
    }
    if (diarization && !validateSpeakerConstraints()) return;

    if (urlFiles.length > 0) {
      for (const urlFile of urlFiles) {
        const success = await uploadUrlFile(urlFile);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (success) state.uppy!.removeFile((urlFile as any).id);
      }
      if (tusFiles.length === 0) {
        toast.success(
          activeType === 'document'
            ? t('transcript-created-successfully')
            : t('caption-created-successfully')
        );
        invalidateAfterUpload();
        dispatch({ type: 'SET_UPLOADING', payload: false });
        setIsSubmitting(false);
        onClose();
        return;
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tusFiles.forEach((file: any) => {
      if (!file.meta.uuid) {
        const existingUuid = fileUuidsRef.current.get(file.id) || uuidv4();
        fileUuidsRef.current.set(file.id, existingUuid);
        state.uppy!.setFileMeta(file.id, { uuid: existingUuid });
      }
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const metadata: any = {
      language: state.selectedLanguage?.key,
      document_type: activeType,
      linked: false,
      folder_id: selectedFolderId !== null ? String(selectedFolderId) : null,
      punctuation: 'true',
      keep_source: 'true',
      multichannel: 'false',
      diarization: diarization ? 'true' : 'false',
      source: 'ui'
    };
    if (diarization) {
      if (state.minSpeakers != null)
        metadata.min_speakers = state.minSpeakers.toString();
      if (state.maxSpeakers != null)
        metadata.max_speakers = state.maxSpeakers.toString();
    }
    if (state.selectedExportTemplate?.id)
      metadata.export_template_id = state.selectedExportTemplate.id.toString();

    state.uppy.setMeta(metadata);
    dispatch({ type: 'SET_UPLOADING', payload: true });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let result: any;
    if (
      (state.resultUppy as { failed?: unknown[] } | null)?.failed?.length ??
      0 > 0
    ) {
      result = await state.uppy!.retryAll();
    } else {
      result = await state.uppy!.upload();
    }
    await checkUploadResult(result);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const checkUploadResult = async (result: any) => {
    dispatch({ type: 'SET_RESULT_UPPY', payload: result });
    if (result.failed && result.failed.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      result.failed.forEach((failed: any) => handleError(failed));
    } else if (result.successful && result.successful.length > 0) {
      toast.success(
        activeType === 'document'
          ? t('transcript-created-successfully')
          : t('caption-created-successfully')
      );
      invalidateAfterUpload();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      result.successful?.forEach((file: any) =>
        state.uppy?.removeFile(file.id)
      );
      onClose();
    }
    dispatch({ type: 'SET_UPLOADING', payload: false });
    setIsSubmitting(false);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleError = (failedUppyFile: any) => {
    try {
      if (failedUppyFile.meta && 'error' in failedUppyFile.meta) {
        toast.error(failedUppyFile.meta.toString());
        return;
      }
      const error = failedUppyFile.error;
      const isNetworkError =
        error?.message?.includes('network') ||
        error?.message?.includes('NetworkError') ||
        error?.message?.includes('Failed to fetch') ||
        error?.message?.includes('blocked') ||
        error?.message?.includes('firewall') ||
        error?.isNetworkError;
      if (isNetworkError) {
        toast.error(
          t('upload-interrupted-check-connection') ||
            'Upload interrupted. Please check your internet connection and try again.'
        );
        return;
      }
      if (
        'tus' in failedUppyFile &&
        failedUppyFile.tus &&
        typeof failedUppyFile.tus === 'object' &&
        'uploadUrl' in failedUppyFile.tus
      ) {
        toast.error(t('server-error-please-try-again-or-contact-support'));
        return;
      }
      toast.error(t('network-error-please-try-again-or-contact-support'));
    } catch {
      toast.error(
        t(
          'unexpected-error-while-uploading-files-please-try-again-or-contact-support'
        )
      );
    }
  };

  const onClose = () => {
    if (!state.uppy) {
      dispatch({ type: 'SET_UPLOADING', payload: false });
      dispatch({ type: 'SET_RESULT_UPPY', payload: null });
      dispatch({ type: 'SET_SELECTED_LANGUAGE', payload: null });
      setType('');
      return;
    }
    if (state.uploading || state.azureBlobProgress > 0) state.uppy.pauseAll();
    const files = state.uppy.getFiles();
    const successfulFiles = files.filter(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (file: any) => file.progress?.uploadComplete
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    successfulFiles.forEach((file: any) => {
      state.uppy!.removeFile(file.id);
      fileUuidsRef.current.delete(file.id);
    });
    dispatch({ type: 'SET_UPLOADING', payload: false });
    dispatch({ type: 'SET_RESULT_UPPY', payload: null });
    dispatch({ type: 'SET_SELECTED_LANGUAGE', payload: null });
    dispatch({ type: 'SET_AZURE_BLOB_PROGRESS', payload: 0 });
    dispatch({ type: 'SET_IS_RECORDING', payload: false });
    dispatch({ type: 'SET_MIN_SPEAKERS', payload: null });
    dispatch({ type: 'SET_MAX_SPEAKERS', payload: null });
    dispatch({ type: 'SET_SELECTED_EXPORT_TEMPLATE', payload: null });
    setSpeakerValidationError('');
    setType('');
  };

  const handleAlignmentUpload = async () => {
    if (!state.selectedLanguage) {
      toast.error(t('please-select-language'));
      return;
    }
    if (!state.alignmentMediaFile || !state.alignmentTranscriptFile) {
      toast.error('Please select both media and transcript files');
      return;
    }
    dispatch({ type: 'SET_UPLOADING', payload: true });
    setIsSubmitting(true);
    dispatch({ type: 'SET_ALIGNMENT_PROGRESS', payload: 0 });
    try {
      const sessionResponse = await createAlignmentSessionMutation.mutateAsync({
        language: state.selectedLanguage.key,
        folder_id: selectedFolderId
      });
      await uploadAlignmentMutation.mutateAsync({
        sessionId: sessionResponse.result.session_id,
        mediaFile: state.alignmentMediaFile,
        transcriptFile: state.alignmentTranscriptFile,
        onProgress: progress =>
          dispatch({ type: 'SET_ALIGNMENT_PROGRESS', payload: progress })
      });
      toast.success(
        'Alignment upload successful! Processing will begin shortly.'
      );
      invalidateAfterUpload();
      dispatch({ type: 'SET_ALIGNMENT_MEDIA_FILE', payload: null });
      dispatch({ type: 'SET_ALIGNMENT_TRANSCRIPT_FILE', payload: null });
      dispatch({ type: 'SET_ALIGNMENT_PROGRESS', payload: 0 });
      onClose();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      toast.error(
        err?.response?.data?.error ||
          err?.message ||
          'Failed to upload alignment files. Please try again.'
      );
    } finally {
      dispatch({ type: 'SET_UPLOADING', payload: false });
      setIsSubmitting(false);
    }
  };

  const handleUploadClick = async () => {
    if (type === 'align') {
      await handleAlignmentUpload();
      return;
    }
    if (hasActiveTusUpload && !useDirectAzureUpload) {
      if (state.isPaused) {
        state.uppy?.resumeAll();
        dispatch({ type: 'SET_IS_PAUSED', payload: false });
      } else {
        state.uppy?.pauseAll();
        dispatch({ type: 'SET_IS_PAUSED', payload: true });
      }
      return;
    }
    if (!canStartUpload) {
      toast.error(t('please-add-a-file-or-finish-recording'));
      return;
    }
    setIsSubmitting(true);
    try {
      if (useDirectAzureUpload) await uploadDirectAzure();
      else await uploadTus();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      toast.error(
        err?.response?.data?.error ||
          err?.message ||
          t(
            'unexpected-error-while-uploading-files-please-try-again-or-contact-support'
          )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const languageOptions =
    models?.result?.map((item: Language) => ({
      label: `${item.name} (${item.key})`,
      value: item.id.toString(),
      is_private: item.is_private,
      keyCode: item.key,
      name: item.name
    })) ?? [];

  const detectedMatch = useMemo(() => {
    if (detection.status !== 'detected') return null;
    return (
      models?.result?.find(
        (item: Language) =>
          item.key.toLowerCase() === detection.language.toLowerCase()
      ) || null
    );
  }, [detection, models]);

  useEffect(() => {
    if (!detectedMatch) return;
    if (userOverrodeLanguageRef.current) return;
    if (detectedMatch.id === state.selectedLanguage?.id) return;
    dispatch({ type: 'SET_SELECTED_LANGUAGE', payload: detectedMatch });
  }, [detectedMatch, state.selectedLanguage]);

  const getNonAlignButtonLabel = () => {
    if (hasActiveTusUpload) return state.isPaused ? t('Resume') : t('Pause');
    if (!canStartUpload) return t('select-or-record-a-file');
    return t('upload');
  };

  const hasActiveUploads =
    state.uploading ||
    isSubmitting ||
    state.azureBlobProgress > 0 ||
    hasActiveTusUpload;
  const hasUnsavedContent = state.hasFiles && !state.uploading && !isSubmitting;
  const isActivelyRecording = state.isRecording;
  const allowEscClose =
    !isActivelyRecording && !hasActiveUploads && !hasUnsavedContent;
  const shouldConfirmBeforeClose =
    isActivelyRecording || hasUnsavedContent || hasActiveUploads;

  const getConfirmMessage = () => {
    if (isActivelyRecording)
      return (
        t('recording-in-progress-confirmation') ||
        'Recording in progress! Closing will lose your recording. Are you sure?'
      );
    if (hasActiveUploads) return t('uploads-in-progress-close-to-pause');
    if (hasUnsavedContent)
      return (
        t('unsaved-files-confirmation') ||
        "You have files that haven't been uploaded yet. Closing will discard them. Are you sure?"
      );
    return '';
  };

  return {
    state,
    dispatch,
    activeType,
    setActiveType,
    diarization,
    setDiarization,
    isSubmitting,
    useDirectAzureUpload,
    setUseDirectAzureUpload,
    hasLargeFiles,
    selectedFolderId,
    setSelectedFolderId,
    speakerValidationError,
    setSpeakerValidationError,
    detection,
    detectedMatch,
    userOverrodeLanguageRef,
    languageOptions,
    folderOptions,
    models,
    hasUrlFiles,
    hasActiveTusUpload,
    canStartUpload,
    primaryUploadDisabled,
    hasActiveUploads,
    allowEscClose,
    shouldConfirmBeforeClose,
    getConfirmMessage,
    validateAlignmentMediaFile,
    validateAlignmentTranscriptFile,
    handleUploadClick,
    getNonAlignButtonLabel,
    onClose
  };
}
